// Hold a session while the user wants the pop-out so a browser that fires
// enterpictureinpicture can ask us to open the window again. Chrome only
// fires that on its own for a camera, microphone, or audible video.

export interface PipMediaSession {
  playbackState: 'none' | 'paused' | 'playing'
  metadata: MediaMetadata | null
  setActionHandler: (action: 'enterpictureinpicture', handler: (() => void) | null) => void
}

export function mediaSessionFromNavigator(
  session: MediaSession | null | undefined,
): PipMediaSession | null {
  if (!session || typeof session.setActionHandler !== 'function') {
    return null
  }
  const setAction = session.setActionHandler.bind(session) as (
    action: string,
    handler: (() => void) | null,
  ) => void
  return {
    get playbackState() {
      return session.playbackState
    },
    set playbackState(value) {
      session.playbackState = value
    },
    get metadata() {
      return session.metadata
    },
    set metadata(value) {
      session.metadata = value
    },
    setActionHandler(action, handler) {
      setAction(action, handler)
    },
  }
}

export function pictureInPictureWindowShown(pipWindow: Window | null): boolean {
  if (!pipWindow || pipWindow.closed) {
    return false
  }
  try {
    return pipWindow.document.visibilityState !== 'hidden'
  } catch {
    return false
  }
}

export function shouldRequestPictureInPictureWindow(input: {
  wantsFloating: boolean
  windowShown: boolean
}): boolean {
  return input.wantsFloating && !input.windowShown
}

export function keepPipSessionAfterClose(input: {
  opening: boolean
  wantsFloating: boolean
  openerHidden: boolean
}): boolean {
  if (input.opening) {
    return true
  }
  return input.wantsFloating && input.openerHidden
}

export function aidaSessionMetadata(): MediaMetadata | null {
  if (typeof MediaMetadata !== 'function') {
    return null
  }
  try {
    return new MediaMetadata({ title: 'Aida' })
  } catch {
    return null
  }
}

export function holdPictureInPictureSession(
  session: PipMediaSession,
  onEnter: () => void,
  metadata: MediaMetadata | null,
): () => void {
  const previousState = session.playbackState
  const previousMetadata = session.metadata
  session.metadata = metadata
  session.playbackState = 'playing'
  let bound = false
  try {
    session.setActionHandler('enterpictureinpicture', () => {
      onEnter()
    })
    bound = true
  } catch {
    bound = false
  }
  return () => {
    if (bound) {
      try {
        session.setActionHandler('enterpictureinpicture', null)
      } catch {
        // The page is going away with the session.
      }
    }
    session.playbackState = previousState
    session.metadata = previousMetadata
  }
}
